import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";
import { FileSpreadsheet, FileText } from "lucide-react";
import { api, errMsg, download } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead, Panel, Empty, SButton } from "@/components/staff/Bits";
import Filters, { clean } from "@/components/staff/Filters";

const LABELS = {
  total_requests: "Total requests", processed: "Requests processed", awaiting_action: "Awaiting action", in_assistance: "Currently being assisted",
  referred: "Referrals", completed: "Completed (outcome documented)", unable_to_proceed: "Unable to proceed", continuing_accompaniment: "Continuing accompaniment",
  followups_scheduled: "Follow-ups scheduled", followups_overdue: "Follow-ups overdue", avg_first_response_hours: "Avg. time to first human response (hours)",
  home_visit_demand: "Demand for home visits", faith_formation_demand: "Demand for faith formation", health_requests: "Health assistance requests", economic_requests: "Economic / livelihood requests",
};
const show = (v) => (v === null || v === undefined ? "<5" : v);

const Table = ({ rows, testid }) => (
  <table className="w-full text-[15px]" data-testid={testid}><tbody>
    {rows.map((r) => <tr key={r.key || r.month} className="border-b border-[#F1EBE3]"><td className="py-2 pr-3">{r.label || r.month}</td><td className="py-2 text-right font-bold">{r.suppressed ? <span className="text-[#605349] font-normal">&lt;5 (suppressed)</span> : r.count}</td></tr>)}
  </tbody></table>
);

export default function Reports() {
  const { can } = useAuth();
  const [filters, setFilters] = useState({});
  const [rep, setRep] = useState(null);
  useEffect(() => { api.get("/reports/summary", { params: clean(filters) }).then((r) => setRep(r.data)).catch((e) => toast.error(errMsg(e))); }, [filters]);
  const exp = async (format) => {
    try { await download("/reports/export", { ...clean(filters), format }, `mabuhay-care-report.${format}`); } catch (e) { toast.error(errMsg(e)); }
  };
  const chart = (rows) => rows.map((r) => ({ name: r.label || r.month, count: r.count ?? 0 }));
  return (
    <div>
      <PageHead title="Pastoral reports" sub="Aggregated, privacy-preserving reports. No names or case details. Groups smaller than 5 are suppressed. Referral or closure is not the same as resolution.">
        {can("reports:export") && <><SButton variant="ghost" data-testid="export-xlsx-button" onClick={() => exp("xlsx")}><FileSpreadsheet className="w-4 h-4" /> Excel</SButton><SButton variant="ghost" data-testid="export-pdf-button" onClick={() => exp("pdf")}><FileText className="w-4 h-4" /> PDF</SButton></>}
      </PageHead>
      <Filters value={filters} onChange={setFilters} />
      {!rep ? <Empty>Loading…</Empty> : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="report-summary">
            {Object.entries(rep.summary).map(([k, v]) => (
              <div key={k} data-testid={`report-${k}`} className="rounded-2xl bg-white border border-[#E7DFD5] p-4"><p className="text-xs text-[#605349] font-semibold">{LABELS[k]}</p><p className="mt-1 text-2xl font-serif-head font-black text-[#4A2E1A]">{k === "avg_first_response_hours" ? (v ?? "—") : show(v)}</p></div>))}
          </div>
          <div className="grid lg:grid-cols-2 gap-5">
            <Panel title="A. Monthly requests" testid="report-monthly">
              {rep.by_month.length === 0 ? <Empty>No data.</Empty> : <div className="h-60"><ResponsiveContainer><LineChart data={chart(rep.by_month)}><CartesianGrid stroke="#F1EBE3" /><XAxis dataKey="name" /><YAxis allowDecimals={false} /><Tooltip /><Line dataKey="count" stroke="#4A2E1A" strokeWidth={2} /></LineChart></ResponsiveContainer></div>}
              <Table rows={rep.by_month} testid="table-monthly" />
            </Panel>
            <Panel title="C. Parish-wide needs by category" testid="report-category">
              <div className="h-60"><ResponsiveContainer><BarChart data={chart(rep.by_category).map((r) => ({ ...r, name: r.name.split(" ")[0] }))}><XAxis dataKey="name" tick={{ fontSize: 12 }} /><YAxis allowDecimals={false} /><Tooltip /><Bar dataKey="count" fill="#3D6346" radius={[6, 6, 0, 0]} /></BarChart></ResponsiveContainer></div>
              <Table rows={rep.by_category} testid="table-category" />
            </Panel>
            <Panel title="B. GKK-level needs" testid="report-gkk">{rep.by_gkk.length === 0 ? <Empty>No data.</Empty> : <Table rows={rep.by_gkk} testid="table-gkk" />}<p className="text-xs text-[#605349] mt-3">Safeguarding cases are excluded from GKK and category breakdowns.</p></Panel>
            <Panel title="D. Response effectiveness — outcomes" testid="report-outcomes">
              <Table rows={rep.outcomes} testid="table-outcomes" />
              <p className="text-sm mt-3">Restricted safeguarding pathway cases: <b>{rep.restricted_pathway.suppressed ? "<5 (suppressed)" : rep.restricted_pathway.count}</b></p>
            </Panel>
          </div>
        </div>
      )}
    </div>
  );
}
